import {
  CARD_STAGE_LABEL_KEYS,
  CARD_STAGE_PILL,
} from "@/modules/dashboard/organizations/utils/organizationOverview";
import { KycStatusPill } from "@/modules/verification/components/KycStatusPill";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { useTranslations } from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { useCardholderCopy } from "../hooks/useCardholderCopy";
import type { CardholderBoardItem } from "../types/cardholderOnboarding.types";
import { ACTION_LABEL_KEYS, cardholderDocument, isQueueAction } from "../utils/cardholderBoard";
import { CardholderProgress } from "./CardholderProgress";

const EMPTY = "—";

export interface CardholderDetailDrawerProps {
  item: CardholderBoardItem | null;
  providerLabel: string;
  canQueue: boolean;
  canWrite: boolean;
  onOpenChange: (open: boolean) => void;
  onAction: (item: CardholderBoardItem) => void;
}

export function CardholderDetailDrawer({
  item,
  providerLabel,
  canQueue,
  canWrite,
  onOpenChange,
  onAction,
}: CardholderDetailDrawerProps) {
  const { t, activeLanguage } = useTranslations("cardholderOnboarding");
  const { t: tOrg } = useTranslations("organizations");
  const copy = useCardholderCopy();
  const dateOptions = { locale: activeLanguage, includeTime: true } as const;

  const action = item?.next_action ?? "none";
  const showAction = !!item && action !== "none" && (!isQueueAction(action) || canQueue);
  const informational = action === "complete_data";

  return (
    <ColocaDrawer
      open={!!item}
      onOpenChange={onOpenChange}
      size="md"
      pinLastChild={false}
      title={item?.name ?? t("unnamed")}
      description={item ? (cardholderDocument(item) ?? undefined) : undefined}
      footer={
        showAction && item ? (
          <ColocaButton
            variant={informational ? "default" : "primary"}
            disabled={!informational && !canWrite}
            className="w-full sm:w-auto"
            onClick={() => onAction(item)}
          >
            {t(ACTION_LABEL_KEYS[action as Exclude<typeof action, "none">])}
          </ColocaButton>
        ) : null
      }
    >
      {item && (
        <div className="space-y-6">
          <CardholderProgress
            item={item}
            providerLabel={providerLabel}
            headline={copy.headline(item, providerLabel)}
          />

          <dl className="grid gap-4 sm:grid-cols-2">
            <Field label={t("detail_verification")}>
              {item.kyc_status ? (
                <KycStatusPill status={item.kyc_status} />
              ) : (
                <span className="text-sm text-muted-foreground">{t("no_kyc")}</span>
              )}
            </Field>
            <Field label={t("detail_card", { provider: providerLabel })}>
              <ColocaStatusPill variant={CARD_STAGE_PILL[item.card_stage]} size="sm" withDot>
                {tOrg(CARD_STAGE_LABEL_KEYS[item.card_stage])}
              </ColocaStatusPill>
              {item.pause_reason && (
                <p className="mt-1 text-xs text-muted-foreground">{copy.pauseReason(item.pause_reason)}</p>
              )}
            </Field>
            <Field label={t("detail_email")}>
              <span className="text-sm break-all">{item.email ?? EMPTY}</span>
            </Field>
            <Field label={t("detail_country")}>
              <span className="text-sm">{item.country ?? EMPTY}</span>
            </Field>
            <Field label={t("detail_updated")}>
              <span className="text-sm">
                {item.card_updated_at ? formatDateTime(item.card_updated_at, dateOptions) : EMPTY}
              </span>
            </Field>
            <Field label={t("detail_created")}>
              <span className="text-sm">{formatDateTime(item.created_at, dateOptions)}</span>
            </Field>
            <Field label={t("detail_identity_id")} className="sm:col-span-2">
              <span className="font-mono text-xs break-all">{item.identity_id}</span>
            </Field>
          </dl>
        </div>
      )}
    </ColocaDrawer>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <dt className="text-xs tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-1">{children}</dd>
    </div>
  );
}
